<template>
  <div class="otp-wrapper">
    <div class="otp-input-group">
      <input
        v-for="(digit, index) in digits"
        :key="index"
        ref="otpInputs"
        type="text"
        class="otp-digit"
        :class="{ bounce: bounceStates[index], shake: shakeStates[index] }"
        :maxlength="1"
        :inputmode="'numeric'"
        :pattern="'[0-9]*'"
        :autofocus="index === 0"
        autocomplete="new-password"
        v-model="digits[index]"
        @input="handleInput($event, index)"
        @keydown="handleKeydown($event, index)"
        @paste="handlePaste"
        @focus="handleFocus($event)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'OtpInput',
  props: {
    digitCount: {
      type: Number,
      default: 6
    },
    autoVerify: {
      type: Boolean,
      default: false
    },
    value: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      digits: [],
      bounceStates: [],
      shakeStates: [],
      autoHideTimeout: null
    }
  },
  computed: {
    isComplete () { return this.digits.every(digit => digit !== null && digit !== '') },
    inputRefs () { return this.$refs.otpInputs || [] }
  },
  watch: {
    value: {
      handler (newVal) {
        if (newVal && newVal !== this.getOTP()) this.setValue(newVal)
      },
      immediate: true
    },
    isComplete (complete) {
      if (complete && this.autoVerify) {
        this.$emit('complete', this.getOTP())
      }
    }
  },
  mounted () {
    this.initializeDigits()
    // Set up refs array after DOM is ready
    this.$nextTick(() => {
      if (this.inputRefs && this.inputRefs[0]) {
        this.inputRefs[0].focus()
      }
    })
  },
  beforeDestroy () {
    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout)
    }
  },
  methods: {
    initializeDigits () {
      // Initialize digits array
      for (let i = 0; i < this.digitCount; i++) {
        this.$set(this.digits, i, null)
        this.$set(this.bounceStates, i, false)
        this.$set(this.shakeStates, i, false)
      }
      // If initial value provided
      if (this.value && this.value.length === this.digitCount) {
        for (let i = 0; i < this.digitCount; i++) {
          this.$set(this.digits, i, this.value.charAt(i))
        }
      }
    },
    handleInput (event, index) {
      let value = event.target.value
      // Allow only single digit
      if (value.length > 1) {
        value = value.charAt(0)
      }

      // Validate only numbers
      if (value && !/^[0-9]$/.test(value)) {
        this.$set(this.digits, index, this.digits[index] || '')
        this.triggerShake(index)
        // Reset input value
        this.$nextTick(() => {
          if (this.inputRefs[index]) {
            this.inputRefs[index].value = this.digits[index] || ''
          }
        })
        return
      }

      // Update digits array
      const newValue = value === '' ? null : value
      this.$set(this.digits, index, newValue)

      // Add bounce animation if value entered
      if (value && value !== '') {
        this.addBounceAnimation(index)
      }

      // Auto-advance to next input if value entered
      if (value && value !== '' && index < this.digitCount - 1) {
        this.focusInput(index + 1)
      }

      // Emit change event
      this.$emit('input', this.getOTP())
    },
    handleKeydown (event, index) {
      // Handle backspace
      if (event.key === 'Backspace') {
        if (!this.digits[index] && index > 0) {
          // Move to previous input if current is empty
          this.focusInput(index - 1, true)
        } else {
          // Clear current digit
          this.$set(this.digits, index, null)
          this.$nextTick(() => {
            if (this.inputRefs[index]) {
              this.inputRefs[index].value = ''
            }
          })

          if (index > 0) {
            this.focusInput(index - 1, true)
          }
        }

        // this.$emit('change', this.getOTP())
        this.$emit('input', this.getOTP())
        event.preventDefault()
      }

      // Handle left arrow
      if (event.key === 'ArrowLeft' && index > 0) {
        this.focusInput(index - 1, true)
        event.preventDefault()
      }

      // Handle right arrow
      if (event.key === 'ArrowRight' && index < this.digitCount - 1) {
        this.focusInput(index + 1, true)
        event.preventDefault()
      }

      // Handle delete key
      if (event.key === 'Delete') {
        this.$set(this.digits, index, null)
        this.$nextTick(() => {
          if (this.inputRefs[index]) {
            this.inputRefs[index].value = ''
          }
        })
        event.preventDefault()
      }
    },
    handlePaste (event) {
      event.preventDefault()
      const pastedData = (event.clipboardData || window.clipboardData).getData('text')
      const cleaned = pastedData.replace(/\D/g, '') // Remove non-digits

      if (cleaned.length > 0) {
        // Fill digits with pasted values
        for (let i = 0; i < Math.min(this.digitCount, cleaned.length); i++) {
          this.$set(this.digits, i, cleaned.charAt(i))
          this.addBounceAnimation(i)
        }

        // Focus on the next empty input or last input
        let nextIndex = Math.min(cleaned.length, this.digitCount - 1)
        if (nextIndex < this.digitCount && !this.digits[nextIndex]) {
          this.focusInput(nextIndex)
        } else if (this.isComplete) {
          if (this.inputRefs[this.digitCount - 1]) {
            this.inputRefs[this.digitCount - 1].blur()
          }
        }
        this.$emit('input', this.getOTP())
      }
    },
    handleFocus (event) {
      event.target.select()
    },
    addBounceAnimation (index) {
      this.$set(this.bounceStates, index, true)
      setTimeout(() => {
        this.$set(this.bounceStates, index, false)
      }, 300)
    },
    triggerShake (index) {
      this.$set(this.shakeStates, index, true)
      setTimeout(() => {
        this.$set(this.shakeStates, index, false)
      }, 400)
    },
    focusInput (index, select = false) {
      this.$nextTick(() => {
        const input = this.inputRefs[index]
        if (input) {
          input.focus()
          if (select) {
            input.select()
          }
        }
      })
    },
    getOTP () {
      return this.digits.map(d => d || '').join('')
    },
    setValue (otpString) {
      const digits = otpString.toString().split('')
      for (let i = 0; i < this.digitCount; i++) {
        if (digits[i] && /^[0-9]$/.test(digits[i])) {
          this.$set(this.digits, i, digits[i])
        } else {
          this.$set(this.digits, i, null)
        }
      }
      // this.$emit('change', this.getOTP())
      this.$emit('input', this.getOTP())
    },
    resetOTP () {
      for (let i = 0; i < this.digitCount; i++) {
        this.$set(this.digits, i, null)
      }
      this.focusInput(0)
    }
  }
}
</script>

<style scoped>
.otp-wrapper {
  background: white;
  border-radius: 24px;
  text-align: center;
  max-width: 500px;
  width: 100%;
  padding: 20px 0;
}

.otp-input-group {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.otp-digit {
  width: 50px;
  height: 60px;
  text-align: center;
  font-size: 32px;
  font-weight: 600;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  background: white;
  transition: all 0.2s ease;
  font-family: monospace;
  margin-bottom: 0!important
}

.otp-digit:focus {
  outline: none;
  border-color: #333;
  box-shadow: 0 0 0 3px rgba(200, 200, 200, 0.2);
  transform: scale(1.02);
}

/* Animation classes */
.otp-digit.bounce {
  animation: bounce 0.3s ease;
}

.otp-digit.shake {
  animation: shake 0.4s ease;
  border-color: #f56565;
}

@keyframes bounce {
  0% { transform: scale(1); }
  50% { transform: scale(1.08); }
  100% { transform: scale(1); }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-5px); }
  75% { transform: translateX(5px); }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive design */
@media (max-width: 500px) {
  .otp-digit {
    width: 45px;
    height: 55px;
    font-size: 24px;
  }
  
  .otp-input-group {
    gap: 8px;
  }
  
  .otp-wrapper {
    padding: 30px 20px;
  }
}
</style>


// WEBPACK FOOTER //
// otp.vue?caedbdd6